import { Injectable } from '@angular/core';
import { Asset, createClient, Entry, EntryCollection, RichTextContent } from 'contentful';
import { defer, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ComponentModel } from '~contentful/models/components/component.model';
import { RichTextModel } from '~contentful/models/core/rich-text.model';
import { ModelDefinitions, ContentModelType } from '../models/model-map';
import { AssetModel } from '../models/core/asset.model';
import { PageModel } from '../models/components/page.model';
import { Model } from '../models/model';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { contentTypeIds } from 'src/constants';

@Injectable()
export class ContentfulService {
  private client = createClient({
    space: environment.contentfulWS.space,
    accessToken: environment.contentfulWS.accessToken,
    environment: environment.contentfulWS.env,
    host: environment.production ? '' : 'preview.contentful.com',
  });

  private cdaClient = createClient({
    space: environment.contentful.space,
    accessToken: environment.contentful.accessToken,
    environment: environment.contentfulEnv,
  });

  constructor(private http: HttpClient) {}

  /**
   * Converts the object and its children into its fields equivalent
   *
   * @param obj the object which is to be parsed
   */
  public static parse(
    obj: string | number | boolean | null | undefined,
  ): string | number | boolean | null | undefined;
  public static parse<T extends Model = Model>(obj: Entry<any>): T;
  public static parse<T extends Model = Model>(obj: Entry<any>[]): T[];
  public static parse(obj: any): any {
    if (Array.isArray(obj)) {
      return obj.map((item) => this.parse(item));
    }

    if (typeof obj !== 'object') {
      return obj;
    }

    if (obj.hasOwnProperty('nodeType')) {
      return this.parseRichTextContent(obj);
    }

    if (!obj.hasOwnProperty('fields')) {
      return obj;
    }

    switch (obj.sys.type) {
      case 'Entry':
        return this.parseEntry(obj);
      case 'Asset':
        return this.parseAsset(obj);
    }
  }

  private static parseAsset(obj: Asset): AssetModel {
    return new AssetModel(obj.fields, obj.sys);
  }

  private static parseRichTextContent(obj: RichTextContent): RichTextModel {
    let target: ComponentModel;
    if (obj.nodeType === 'embedded-entry-block') {
      target = this.parseEntry(obj.data.target as any);
    }

    let models: RichTextModel[];
    if (obj.content) {
      models = obj.content.map((content) => this.parseRichTextContent(content));
    }

    return new RichTextModel(obj, target, models);
  }

  private static parseEntry(obj: Entry<any>): ComponentModel {
    const constructor = ModelDefinitions[obj.sys.contentType.sys.id];

    if (!constructor) {
      const error = new Error('Error retrieving constructor for ID: ' + obj.sys.contentType.sys.id);
      window.zbm?.reportError(error);
      throw error;
    }

    const result: any = {};

    Object.keys(obj.fields).forEach((key) => {
      result[key] = this.parse(obj.fields[key]);
    });

    return new constructor(result, obj.sys);
  }

  public static parseCollection<T extends Model = Model>(collection: EntryCollection<any>): T[] {
    return this.parse<T>(collection.items);
  }

  getEntry<T>(id: string, query?: any) {
    return defer(() => this.client.getEntry<T>(id, query)).pipe(
      map((entry) => ContentfulService.parse(entry)),
    );
  }

  getEntries<T>(contentType: ContentModelType, query: object = {}): Observable<EntryCollection<T>> {
    return defer(() =>
      this.client.getEntries<T>({
        content_type: contentType,
        ...query,
      }),
    );
  }

  getPage(slug: string) {
    return this.getEntries(ContentModelType.Page, {
      include: 10,
      fields: { slug },
    }).pipe(
      map((collection) => ContentfulService.parseCollection<PageModel>(collection)),
      map((items) => {
        if (items.length === 0) {
          const error = new Error(`Could not find page with slug ${slug}`);
          window.zbm?.reportError(error);
          throw error;
        }

        items[0].generateContexts();

        return items[0];
      }),
    );
  }

  async getBackup(contentType: string): Promise<Entry<any>[]> {
    try {
      const s3Bucket = environment.s3Bucket;
      const directory = environment.contentful.backupDirectory;
      const url = `${s3Bucket}/${directory}/${contentType}.json`;
      return await this.http.get<Entry<any>[]>(url).toPromise<Entry<any>[]>();
    } catch (e) {
      console.error(e);
      Promise.resolve(null);
    }
  }

  async get(contentType: string, query?: object): Promise<Entry<any>[]> {
    try {
      const res = await this.cdaClient.getEntries(
        Object.assign(
          {
            content_type: contentTypeIds[contentType],
            resolveLinks: true,
          },
          query,
        ),
      );

      return res.items;
    } catch (e) {
      console.error(e);
      return await this.getBackup(contentType);
    }
  }
}
